export interface ChannelThemeInput {
  mainColor?: string | null;
  secondaryColor?: string | null;
  headerImage?: string | null;
  fontStyle?: string | null;
  generalColor?: string | null;
}

export interface ChannelTheme {
  mainColor: string;
  secondaryColor: string;
  headerImage: string;
  fontStyle: string;
}

const HEX_SHORT_REGEX = /^#([0-9a-fA-F]{3})$/;
const HEX_FULL_REGEX = /^#([0-9a-fA-F]{6})$/;

const FALLBACK_MAIN = "#1976D2";
const FALLBACK_SECONDARY = "#FFFFFF";

export const DEFAULT_CHANNEL_THEME: ChannelTheme = {
  mainColor: FALLBACK_MAIN,
  secondaryColor: FALLBACK_SECONDARY,
  headerImage: "",
  fontStyle: "Arial",
};

const sanitizeHex = (value?: string | null): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (HEX_FULL_REGEX.test(trimmed)) {
    return `#${trimmed.slice(1).toUpperCase()}`;
  }
  const shortMatch = trimmed.match(HEX_SHORT_REGEX);
  if (shortMatch) {
    const [, rgb] = shortMatch;
    const [r, g, b] = rgb.split("");
    return `#${`${r}${r}${g}${g}${b}${b}`.toUpperCase()}`;
  }
  return null;
};

export const normalizeHexColor = (value: string | undefined | null, fallback: string): string => {
  const sanitizedFallback = sanitizeHex(fallback) ?? FALLBACK_MAIN;
  const sanitizedValue = sanitizeHex(value);
  return sanitizedValue ?? sanitizedFallback;
};

const isNonEmpty = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

export const sanitizeChannelTheme = (
  ...sources: Array<ChannelThemeInput | undefined | null>
): ChannelTheme => {
  const result: ChannelTheme = { ...DEFAULT_CHANNEL_THEME };

  sources.forEach((source) => {
    if (!source) return;

    if (isNonEmpty(source.mainColor)) {
      result.mainColor = normalizeHexColor(source.mainColor, result.mainColor);
    }

    const secondaryCandidate = isNonEmpty(source.secondaryColor)
      ? source.secondaryColor
      : isNonEmpty(source.generalColor)
      ? source.generalColor
      : undefined;

    if (secondaryCandidate) {
      result.secondaryColor = normalizeHexColor(secondaryCandidate, result.secondaryColor);
    }

    if (isNonEmpty(source.headerImage)) {
      result.headerImage = source.headerImage.trim();
    }

    if (isNonEmpty(source.fontStyle)) {
      result.fontStyle = source.fontStyle.trim();
    }
  });

  return result;
};

export const invertHexColor = (
  hex: string,
  fallback: string = DEFAULT_CHANNEL_THEME.secondaryColor
): string => {
  const base = normalizeHexColor(hex, fallback);
  const numeric = parseInt(base.slice(1), 16);
  const inverted = (0xffffff ^ numeric).toString(16).padStart(6, "0").toUpperCase();
  return `#${inverted}`;
};

export const isHexColorLight = (
  hex: string,
  fallback: string = DEFAULT_CHANNEL_THEME.secondaryColor
): boolean => {
  const base = normalizeHexColor(hex, fallback);
  const numeric = parseInt(base.slice(1), 16);
  const r = (numeric >> 16) & 0xff;
  const g = (numeric >> 8) & 0xff;
  const b = numeric & 0xff;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
};
